import { ResourceName } from "./CatalogAdmin.types.tsx";

export const PAGE_SIZE = 50;
export const LOOKUP_PAGE_SIZE = 200;
export const SEARCH_DELAY_MS = 300;

export type FieldKind = "text" | "number" | "integer" | "integers";

export interface FieldSpec {
  name: string;
  kind: FieldKind;
  required?: boolean;
  nullable?: boolean;
  maxLength?: number;
  min?: number;
  unit?: string;
}

export interface ResourceSpec {
  name: ResourceName;
  searchField: string;
  columns: string[];
  fields: FieldSpec[];
  csvHeader: string[];
}

export const CHILLER_GENERAL: FieldSpec[] = [
  { name: "model", kind: "text", required: true, maxLength: 150 },
  { name: "size", kind: "text", required: true, maxLength: 4 },
  { name: "nominal_capacity", kind: "number", required: true },
  { name: "compressor_motor_type", kind: "text", nullable: true, maxLength: 50 },
  { name: "condenser_type", kind: "text", nullable: true, maxLength: 50 },
  { name: "fan_motor_type", kind: "text", nullable: true, maxLength: 50 },
  { name: "coil_type", kind: "text", nullable: true, maxLength: 50 },
  { name: "condensing_approach", kind: "number" },
  { name: "heat_exchanger_approach", kind: "number" },
];

export const CHILLER_REFERENCES: FieldSpec[] = [
  { name: "system_id", kind: "integer", required: true },
  { name: "refrigerant_id", kind: "integer", required: true },
  { name: "voltage_id", kind: "integer", required: true },
  { name: "efficiency_id", kind: "integer", required: true },
  { name: "technology_id", kind: "integer" },
  { name: "heat_exchanger_id", kind: "integer" },
];

export const COMPRESSOR_SCALARS: FieldSpec[] = [
  { name: "model", kind: "text", required: true, maxLength: 60 },
  { name: "refrigerant", kind: "text", required: true, maxLength: 20 },
  { name: "lra", kind: "number", required: true },
  { name: "rla", kind: "number", required: true },
];

export const REFERENCES: Record<string, ResourceName> = {
  system_id: "systems",
  refrigerant_id: "refrigerants",
  voltage_id: "voltages",
  technology_id: "technologies",
  heat_exchanger_id: "heat-exchangers",
  efficiency_id: "efficiencies",
};

const simple = (
  name: ResourceName,
  searchField: string,
  columns: string[],
  fields: FieldSpec[]
): ResourceSpec => ({
  name,
  searchField,
  columns,
  fields,
  csvHeader: fields.map((field) => field.name),
});

export const RESOURCES: ResourceSpec[] = [
  {
    name: "chillers",
    searchField: "model",
    columns: [
      "model",
      "nominal_capacity",
      "size",
      "system_id",
      "refrigerant_id",
      "voltage_id",
      "technology_id",
    ],
    fields: [],
    csvHeader: [
      ...CHILLER_GENERAL.map((field) => field.name),
      ...CHILLER_REFERENCES.map((field) => field.name),
      "circuits",
      "inverter",
    ],
  },
  {
    name: "compressors",
    searchField: "model",
    columns: ["model", "refrigerant", "lra", "rla"],
    fields: [],
    csvHeader: [...COMPRESSOR_SCALARS.map((field) => field.name), "rt_c", "rt_w", "rt_a", "rt_m"],
  },
  simple(
    "systems",
    "name",
    ["name", "type", "system_name", "code"],
    [
      { name: "name", kind: "text", required: true, maxLength: 100 },
      { name: "type", kind: "text", required: true, maxLength: 50 },
      { name: "system_name", kind: "text", required: true, maxLength: 50 },
      { name: "code", kind: "text", required: true, maxLength: 10 },
    ]
  ),
  simple(
    "voltages",
    "name",
    ["name", "volts", "phase", "frequency", "code"],
    [
      { name: "name", kind: "text", required: true, maxLength: 50 },
      { name: "volts", kind: "integers", required: true },
      { name: "phase", kind: "integer", required: true, min: 1 },
      { name: "frequency", kind: "integer", required: true, min: 1 },
      { name: "code", kind: "text", required: true, maxLength: 10 },
    ]
  ),
  simple(
    "technologies",
    "name",
    ["name", "code"],
    [
      { name: "name", kind: "text", required: true, maxLength: 50 },
      { name: "code", kind: "text", maxLength: 10 },
    ]
  ),
  simple(
    "refrigerants",
    "name",
    ["name", "code"],
    [
      { name: "name", kind: "text", required: true, maxLength: 100 },
      { name: "code", kind: "text", required: true, maxLength: 10 },
    ]
  ),
  simple(
    "heat-exchangers",
    "type",
    ["type", "approach_type", "code"],
    [
      { name: "type", kind: "text", required: true, maxLength: 50 },
      { name: "approach_type", kind: "text", nullable: true, maxLength: 50 },
      { name: "code", kind: "text", required: true, maxLength: 10 },
    ]
  ),
  simple(
    "efficiencies",
    "name",
    ["name", "code"],
    [
      { name: "name", kind: "text", required: true, maxLength: 50 },
      { name: "code", kind: "text", maxLength: 10 },
    ]
  ),
  simple(
    "locations",
    "city",
    ["city", "amsl", "dry_bulb_celsius", "wet_bulb_celsius"],
    [
      { name: "city", kind: "text", required: true, maxLength: 100 },
      { name: "latitude", kind: "text", nullable: true, maxLength: 20 },
      { name: "longitude", kind: "text", nullable: true, maxLength: 20 },
      { name: "amsl", kind: "number", required: true, unit: "m" },
      { name: "pressure_millibar", kind: "number", unit: "mbar" },
      { name: "average_temperature_celsius", kind: "number", unit: "°C" },
      { name: "dry_bulb_celsius", kind: "number", required: true, unit: "°C" },
      { name: "wet_bulb_celsius", kind: "number", unit: "°C" },
    ]
  ),
];

export const resourceSpec = (name: ResourceName): ResourceSpec =>
  RESOURCES.find((spec) => spec.name === name) as ResourceSpec;

export const isSimple = (name: ResourceName): boolean => resourceSpec(name).fields.length > 0;
